import type { Page } from '../../types.js';

/**
 * The commercial core, and the page most likely to be read by somebody who will
 * quote from it.
 *
 * Two constraints shaped it. **There is no mid** — the customer's two sides are
 * built outward from the vendor's own two sides, and a page that implies otherwise
 * would describe a defect that was deliberately removed. And **this page is
 * public**: the platform legs are our margin on a tenant, so no figure for them
 * appears anywhere here, including in the worked example, which is computed from
 * the tenant legs alone and says so.
 */
const page: Page = {
  slug: 'pricing-and-commission',
  title: 'Pricing and commission',
  lede:
    'Four commission legs price one metal in one currency. Two are yours, visible and editable at both ends. Two are ours and agreed commercially. Nothing is priced from a midpoint, because a midpoint is not a price anyone can trade at.',
  group: 'Commercials',
  blocks: [
    {
      kind: 'callout',
      tone: 'stop',
      title: 'There is no mid price',
      text: 'A vendor quotes two prices, not one: the price we pay to source a gram and the price we receive when we sell one. Commission is applied to those two numbers, on their own sides. Marking up a midpoint throws the vendor spread away and can quote a customer below the price we must buy at, on every single trade.',
    },
    {
      kind: 'stats',
      items: [
        { value: '4', label: 'Commission legs', sub: 'Buy and sell, for each party', accent: 'amber' },
        { value: '2 × 2', label: 'Configured separately', sub: 'Per metal and per currency', accent: 'amber' },
        { value: '2', label: 'Ways to express a leg', sub: 'A percentage, or an amount per gram', accent: 'amber' },
        { value: '6dp', label: 'Rate precision', sub: 'Exact decimals, never floating point', accent: 'teal' },
      ],
    },

    { kind: 'heading', text: 'How a customer rate is built' },
    {
      kind: 'flow',
      accent: 'amber',
      steps: [
        { index: 'ask', title: 'Vendor ask', sub: 'What sourcing a gram costs' },
        { index: '+', title: 'Platform leg', sub: 'Ours, agreed commercially' },
        { index: '+', title: 'Your leg', sub: 'Yours, and you set it' },
        { index: '=', title: 'Customer buy', sub: 'Rounded up, six decimals' },
      ],
    },
    {
      kind: 'flow',
      accent: 'amber',
      steps: [
        { index: 'bid', title: 'Vendor bid', sub: 'What offloading a gram realises' },
        { index: '−', title: 'Platform leg', sub: 'Ours, agreed commercially' },
        { index: '−', title: 'Your leg', sub: 'Yours, and you set it' },
        { index: '=', title: 'Customer sell', sub: 'Rounded down, six decimals' },
      ],
    },
    {
      kind: 'prose',
      text: 'The buy rate rounds up and the sell rate rounds down, so neither can drift onto the wrong side of the price the trade has to be sourced or realised at. That is the only lossy step in the whole calculation.',
    },

    { kind: 'heading', text: 'The four legs' },
    {
      kind: 'table',
      columns: ['Leg', 'Applies to', 'Who sets it', 'Who can see it'],
      rows: [
        ['**Platform buy**', 'The vendor ask, on a purchase', 'Agreed with us commercially', 'Nuqi only'],
        ['**Platform sell**', 'The vendor bid, on a sale', 'Agreed with us commercially', 'Nuqi only'],
        ['**Your buy**', 'The vendor ask, on a purchase', 'You', 'You and Nuqi'],
        ['**Your sell**', 'The vendor bid, on a sale', 'You', 'You and Nuqi'],
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'The platform pair is structurally out of reach, not merely withheld',
      text: 'The configuration your surfaces receive is typed so that it cannot carry a platform leg at all, and the approvals queue redacts it as well. A leg reaching a tenant-facing response is a refused request rather than a disclosure.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'A percentage, or an amount per gram' },
    {
      kind: 'prose',
      text: 'Each leg is expressed one of two ways, chosen per metal and per currency. Neither basis works alone across the range, which is why the choice is not made once and applied globally.',
    },
    {
      kind: 'table',
      columns: ['Basis', 'What it means', 'What it is good at', 'Where it goes wrong'],
      rows: [
        [
          'Percentage',
          'A share of the vendor price on that side',
          'Scales with the metal, so one setting is sane for gold and silver at once',
          'Cannot cover a fixed per-order cost on a very small ticket',
        ],
        [
          'Amount per gram',
          'A fixed number of currency units for each gram',
          'Covers a fixed settlement cost regardless of ticket size',
          'The same figure means very different things on different metals',
        ],
      ],
    },
    {
      kind: 'callout',
      tone: 'warn',
      title: 'The same number is a rounding error on gold and a business-ending markup on silver',
      text: 'A flat 2.00 per gram is roughly 0.39% of gold at around 515 per gram, and roughly 26.7% of silver at around 7.49 per gram. That is why the basis is chosen per metal and per currency, and why the platform defaults are percentages rather than fixed amounts.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'A worked example' },
    {
      kind: 'prose',
      text: 'Gold in dirhams, on a bank still using the platform default of 0.50% on each of its own two legs. The vendor prices below are illustrative. The platform legs are set commercially and are deliberately not shown, so the two results are the rate before ours is added.',
    },
    {
      kind: 'split',
      left: {
        title: 'Buy side',
        body: [
          {
            kind: 'table',
            columns: ['', 'AED per gram'],
            rows: [
              ['Vendor ask', '512.400000'],
              ['Platform buy leg', 'Set commercially — not shown'],
              ['Your buy leg, 0.50% of the ask', '2.562000'],
              ['**Customer buy, before the platform leg**', '**514.962000**'],
            ],
          },
        ],
      },
      right: {
        title: 'Sell side',
        body: [
          {
            kind: 'table',
            columns: ['', 'AED per gram'],
            rows: [
              ['Vendor bid', '509.850000'],
              ['Platform sell leg', 'Set commercially — not shown'],
              ['Your sell leg, 0.50% of the bid', '2.549250'],
              ['**Customer sell, before the platform leg**', '**507.300750**'],
            ],
          },
        ],
      },
    },
    {
      kind: 'prose',
      text: 'Adding the platform legs moves the customer buy up and the customer sell down from those two figures. Your own leg is applied to the vendor price on that side, so it is not affected by whatever the platform leg is.',
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'Any "spot" figure a screen shows is a label',
      text: 'Where a rate card shows a single reference price, it is the midpoint of the customer\'s own buy and sell — a chart axis, not something anyone can transact at. Nothing in the pricing calculation reads it.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Fees are not commission, and they are kept apart on purpose' },
    {
      kind: 'table',
      columns: ['', 'Commission', 'Flat fee', 'Percentage fee'],
      rows: [
        ['Where it sits', 'Inside the rate', 'A separate line on the order', 'A separate line on the order'],
        ['What the customer sees', 'A rate, with no fee to read', 'An explicit fee amount', 'An explicit fee amount'],
        ['How it is expressed', 'Percentage or amount per gram, per leg', 'A fixed amount per order', 'Basis points on the cash leg'],
        ['Where it settles', 'A commission account', 'A fee account', 'A fee account'],
        ['On a buy', 'Raises the rate', 'Added to the total', 'Added to the total'],
        ['On a sell', 'Lowers the rate', 'Deducted from the proceeds', 'Deducted from the proceeds'],
      ],
    },
    {
      kind: 'prose',
      text: 'They settle to different accounts because a bank reconciles those accounts independently. Folding a fee into a commission would move revenue between two lines that finance teams compare against each other.',
    },

    { kind: 'heading', text: 'Limits, which we enforce rather than display' },
    {
      kind: 'table',
      columns: ['Limit', 'Platform default', 'Measured against'],
      rows: [
        ['Minimum order', 'AED 100.00', 'The customer-facing total, fee included'],
        ['Maximum order', 'AED 500,000.00', 'The customer-facing total, fee included'],
        ['Daily buy cap, per customer', 'AED 1,000,000.00', 'Everything that customer bought in the window'],
        ['Daily sell cap, per customer', 'AED 1,000,000.00', 'Everything that customer sold in the window'],
        ['Open quotes, per customer', '5', 'Stops quote-spam being a cheap way to load the system'],
        ['How long a quote stays firm', '30 seconds', 'From the moment it is issued'],
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'Limits are stated against what appears on a statement',
      text: 'Not against the pre-fee metal value. That is the number a compliance team wrote the limit about, so it is the number checked — and it is checked on our side on every order, not only in the screen that disabled the button.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Configurations the platform refuses to serve' },
    {
      kind: 'list',
      items: [
        'A negative leg. A rebate is expressed by lowering the other party\'s leg, never by a minus sign that only shows up as a loss once a real price is applied to it.',
        'A leg that would put the customer buy below the vendor ask, or the customer sell above the vendor bid. Checked against the live price on every quote, not only when the number was typed.',
        'A leg that would take the customer sell to zero or below — the case a fixed amount per gram creates on a cheap metal.',
        'A metal and currency pair that is enabled but has no commission configured. It fails loudly rather than quietly quoting at cost.',
        'A vendor price that is missing, zero, or crossed. A bad tick is a data error, not a price.',
      ],
    },
    {
      kind: 'prose',
      text: 'Refusing costs one failed quote. Serving costs the margin on every trade until somebody reads a month-end report, which is why the check runs against the real price on every quote rather than once at configuration time.',
    },

    { kind: 'divider' },
    {
      kind: 'cards',
      columns: 3,
      items: [
        {
          title: 'Operations',
          text: 'How a commission change is proposed, approved and applied, and how long it takes to reach a live app.',
          href: '/business/operations',
          accent: 'teal',
        },
        {
          title: 'How it works',
          text: 'Where those rates appear in the customer journey, and how rounding is applied around them.',
          href: '/business/how-it-works',
          accent: 'blue',
        },
        {
          title: 'Rollout',
          text: 'Agreeing your commercial settings is one of the two things that most often holds up a launch.',
          href: '/business/rollout',
          accent: 'teal',
        },
      ],
    },
  ],
};

export default page;
